/**
 * **Laden** — `/anlagen/{id}/laden?ladepunkt=` und `/wohnung/{unitId}/laden?ladepunkt=`
 * (HM-119, ADR 0036, `ui_kits/dashboard/LadenScreen.jsx`).
 *
 * **Warum es diese Seite gibt:** Die Steuerung liegt seit HM-110 am Gerät, zwei
 * Ebenen tief in der Verwaltung — der richtige Ort für Ampere und Phasen, der
 * falsche für „heute bitte nur mit Sonne". Laden ist täglicher Gebrauch und
 * steht deshalb in der Navigation neben Strom. Und im Mieterstrom ist sie die
 * **einzige** Stelle für den Mieter: Die Geräteverwaltung gehört dem Vermieter.
 *
 * **Zwei Einstiege, eine Seite.** Der Besitzer sieht jeden Ladepunkt der Anlage,
 * der Bewohner die seiner Wohnung; was sie bedienen dürfen, sagt die API
 * (`controllable`). Den Weg zu Ampere, Phasen und Verlauf bekommt nur, wer die
 * Geräteverwaltung erreicht.
 *
 * **Mehrere Ladepunkte, einer zur Zeit.** Im Mieterstrom hat eine Anlage oft
 * einen je Stellplatz; die Wahl steht in der Adresse, damit „der Ladepunkt in
 * der Tiefgarage" ein Link ist.
 */
import { Card, EmptyState, icons } from "@hausmeter/ui";
import { ChargingPanel } from "../components/charge-point.js";
import type { ChargePoint } from "../lib/charge-point.js";
import { navigate, routePath, type Route } from "../lib/router.js";
import { useSiteChargePoints, useUnitChargePoints } from "../lib/queries.js";

export function ChargingScreen({
  siteId,
  chargePointId,
}: {
  siteId: string;
  chargePointId: string | null;
}) {
  const points = useSiteChargePoints(siteId);
  return (
    <ChargingView
      points={points.data?.items}
      pending={points.isPending}
      failed={points.isError}
      chargePointId={chargePointId}
      routeTo={(id) => ({ kind: "charging", siteId, chargePointId: id })}
      onOpenDevice={(deviceId) => navigate({ kind: "device", siteId, deviceId, tab: "control" })}
      emptyText="Ein Ladepunkt erscheint hier, sobald das HausMeter Gateway eine Wallbox aus evcc meldet und du sie in der Geräteverwaltung zugelassen hast."
    />
  );
}

export function ResidentChargingScreen({
  unitId,
  chargePointId,
}: {
  unitId: string;
  chargePointId: string | null;
}) {
  const points = useUnitChargePoints(unitId);
  return (
    <ChargingView
      points={points.data?.items}
      pending={points.isPending}
      failed={points.isError}
      chargePointId={chargePointId}
      routeTo={(id) => ({ kind: "resident-charging", unitId, chargePointId: id })}
      emptyText="Zu deiner Wohnung gehört kein Ladepunkt, den du bedienen kannst. Hängt eine Wallbox hinter deinem Zähler, ordnet der Vermieter ihren Zähler deiner Wohnung zu."
    />
  );
}

function ChargingView({
  points,
  pending,
  failed,
  chargePointId,
  routeTo,
  onOpenDevice,
  emptyText,
}: {
  points: readonly ChargePoint[] | undefined;
  pending: boolean;
  failed: boolean;
  chargePointId: string | null;
  routeTo: (chargePointId: string) => Route;
  onOpenDevice?: (deviceId: string) => void;
  emptyText: string;
}) {
  if (pending) return <Card padding="sm">Wird geladen …</Card>;
  if (failed || points === undefined) {
    return (
      <Card>
        <EmptyState
          title="Die Ladepunkte konnten nicht geladen werden"
          icon={<icons.IconPlug size={30} />}
          size="md"
        >
          Versuch es in einem Augenblick erneut.
        </EmptyState>
      </Card>
    );
  }
  if (points.length === 0) {
    return (
      <Card>
        <EmptyState title="Kein Ladepunkt" icon={<icons.IconPlug size={30} />} size="md">
          {emptyText}
        </EmptyState>
      </Card>
    );
  }

  /* Eine Kennung, die es hier nicht gibt, heißt dasselbe wie keine: der erste. */
  const point = points.find((entry) => entry.deviceId === chargePointId) ?? points[0]!;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)", maxWidth: 880 }}>
      {points.length > 1 && (
        <nav aria-label="Ladepunkte" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {points.map((entry) => {
            const route = routeTo(entry.deviceId);
            const active = entry.deviceId === point.deviceId;
            return (
              <a
                key={entry.deviceId}
                href={routePath(route)}
                aria-current={active ? "page" : undefined}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
                    return;
                  event.preventDefault();
                  navigate(route);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 14px",
                  borderRadius: "var(--radius-pill)",
                  textDecoration: "none",
                  fontSize: 13.5,
                  fontWeight: active ? 700 : 500,
                  border: active
                    ? "2px solid var(--hm-green-500)"
                    : "1px solid var(--border-default)",
                  background: active ? "var(--hm-green-50)" : "var(--surface-card)",
                  color: active ? "var(--hm-green-700)" : "var(--text-body)",
                }}
              >
                <icons.IconPlug size={15} />
                {entry.name}
                {entry.siteAreaName !== null && (
                  <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>
                    · {entry.siteAreaName}
                  </span>
                )}
              </a>
            );
          })}
        </nav>
      )}
      <ChargingPanel
        key={point.deviceId}
        point={point}
        onOpenDevice={onOpenDevice === undefined ? undefined : () => onOpenDevice(point.deviceId)}
      />
    </div>
  );
}

